import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useBetaSignup } from '../context/BetaSignupContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, TrendingUp, Shield, Lock, Compass, Zap, Sparkles, Search, Globe, Wallet } from 'lucide-react';
import { investmentStrategies } from '../mockData';

const StrategyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openModal } = useBetaSignup();
  const strategy = investmentStrategies.find(s => s.id === id);

  const iconMap = {
    Shield: Shield,
    Lock: Lock,
    Compass: Compass,
    TrendingUp: TrendingUp,
    Zap: Zap,
    Sparkles: Sparkles,
    Search: Search,
    Globe: Globe,
    Wallet: Wallet
  };

  if (!strategy) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-4">Strategy Not Found</h1>
          <Link to="/offerings">
            <Button>Back to Offerings</Button>
          </Link>
        </div>
      </div>
    );
  }

  const Icon = iconMap[strategy.icon] || TrendingUp;

  return (
    <div className="min-h-screen bg-[#F5F3ED]">
      {/* Header */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-[#1a1a3e] mb-6 transition-colors"
            data-testid="strategy-back-btn"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </button>
          
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8">
            <div>
              <div className="inline-block px-3 py-1 bg-[#FF8C42] text-white text-xs font-semibold rounded-full mb-3">
                {strategy.category}
              </div>
              <h1 className="text-5xl font-light mb-4" style={{ fontFamily: 'Georgia, serif' }}>{strategy.name}</h1>
              <p className="text-xl text-gray-700 max-w-3xl">{strategy.description}</p>
            </div>
            <Button 
              size="lg" 
              className="bg-[#1a1a3e] hover:bg-[#252550] mt-6 lg:mt-0"
              onClick={openModal}
              data-testid="strategy-invest-btn"
            >
              Invest Now
            </Button>
          </div>

          {/* Strategy Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-6 bg-white">
              <div className="text-sm text-gray-600 mb-1">Target Return</div>
              <div className="text-3xl font-bold text-green-600">{strategy.targetReturn}</div>
            </Card>
            <Card className="p-6 bg-white">
              <div className="text-sm text-gray-600 mb-1">Liquidity</div>
              <div className="text-3xl font-bold">{strategy.liquidity}</div>
            </Card>
            <Card className="p-6 bg-white">
              <div className="text-sm text-gray-600 mb-1">Minimum Investment</div>
              <div className="text-3xl font-bold">${strategy.minInvestment}</div>
            </Card>
            <Card className="p-6 bg-white">
              <div className="text-sm text-gray-600 mb-1">Risk Level</div>
              <div className="text-3xl font-bold">{strategy.riskLevel}</div>
            </Card>
          </div>
        </div>
      </section>

      {/* Strategy Details - No portfolio holdings, description only */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Strategy Icon and Visual */}
            <Card className="p-8 bg-white flex flex-col items-center justify-center">
              <div className="w-40 h-40 mb-6 flex items-center justify-center">
                <Icon className="w-40 h-40" strokeWidth={1} style={{ color: '#FF8C42' }} />
              </div>
              <h3 className="text-2xl font-light mb-2" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                {strategy.name} Strategy
              </h3>
              <p className="text-gray-600 text-center font-light">
                {strategy.category} approach for {strategy.riskLevel.toLowerCase()} risk tolerance
              </p>
            </Card>

            {/* Asset Allocation Overview */}
            <Card className="p-8 bg-white">
              <h3 className="text-2xl font-light mb-6" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                Asset Allocation
              </h3>
              <div className="space-y-4">
                {strategy.allocation.map((alloc, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <span className="text-gray-700 font-light">{alloc.category}</span>
                    <div className="flex items-center gap-4">
                      <div className="w-40 bg-gray-200 rounded-full h-3">
                        <div
                          className="bg-[#FF8C42] h-3 rounded-full transition-all duration-500"
                          style={{ width: `${alloc.percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-lg font-semibold w-12 text-right">{alloc.percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Strategy Details Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
            <Card className="p-8 bg-white">
              <h3 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                Investment Approach
              </h3>
              <p className="text-gray-700 mb-4 font-light leading-relaxed">
                This strategy employs an active management approach, carefully selecting high-quality
                assets with strong fundamentals and growth potential.
              </p>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-3 text-[#FF8C42]">•</span>
                  <span className="font-light">Diversified across multiple sectors and asset classes</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-[#FF8C42]">•</span>
                  <span className="font-light">Regular rebalancing to maintain optimal allocation</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-[#FF8C42]">•</span>
                  <span className="font-light">Focus on long-term value creation</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-3 text-[#FF8C42]">•</span>
                  <span className="font-light">Risk-adjusted returns optimisation</span>
                </li>
              </ul>
            </Card>

            <Card className="p-8 bg-white">
              <h3 className="text-2xl font-light mb-4" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                Key Information
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-600 font-light">Minimum Investment</span>
                  <span className="font-semibold">${strategy.minInvestment}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-600 font-light">Management Fee</span>
                  <span className="font-semibold">{strategy.fee}%</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-600 font-light">Success Fee</span>
                  <span className="font-semibold">{strategy.successFee}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-600 font-light">Risk Level</span>
                  <span className="font-semibold">{strategy.riskLevel}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-gray-600 font-light">Rebalancing</span>
                  <span className="font-semibold">Quarterly</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-light">Liquidity</span>
                  <span className="font-semibold">{strategy.liquidity}</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Portfolio Holdings - Locked, trigger beta modal */}
          <Card className="mt-8 p-8 bg-white">
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-6">
                <Lock className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-2xl font-light mb-3" style={{ fontFamily: 'Georgia, serif', color: '#1a1a3e' }}>
                Portfolio Holdings
              </h3>
              <p className="text-gray-600 font-light max-w-md mb-6">
                Detailed portfolio holdings and allocations are available to beta members. 
                Join our early access program to see the full breakdown.
              </p>
              <Button 
                onClick={openModal}
                className="bg-[#FF8C42] hover:bg-[#FFB366] rounded-full px-8"
                data-testid="strategy-unlock-portfolio-btn"
              >
                Join Beta to Unlock
              </Button>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#1a1a3e] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Ready to invest in {strategy.name}?
          </h2>
          <p className="text-xl text-gray-400 mb-8 font-light">
            Start building wealth with professional management and transparent pricing.
          </p>
          <Button 
            size="lg" 
            className="bg-[#FF8C42] text-white hover:bg-[#FFB366] rounded-full px-10"
            onClick={openModal}
            data-testid="strategy-cta-btn"
          >
            GET STARTED
          </Button>
        </div>
      </section>
    </div>
  );
};

export default StrategyDetail;
